export const formatPYG = (amount) => {
  if (amount === null || amount === undefined) return "₲ 0";
  const num = Number(amount);
  if (isNaN(num)) return "₲ 0";
  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency: "PYG",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatPYGUnit = (amount) => {
  // Precio UNITARIO: entero exacto sin decimales, fraccionario con 2 decimales,
  // para que Cantidad × Precio Unitario mostrado coincida con el Total mostrado.
  if (amount === null || amount === undefined) return "₲ 0";
  const num = Number(amount);
  if (isNaN(num)) return "₲ 0";
  const rounded = Math.round(num * 100) / 100;
  const decimals = rounded === Math.round(rounded) ? 0 : 2;
  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency: "PYG",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

// Fix BN (2026-09-08): "Mantener un solo formato de numeración o precisión, has
// dos digitos despues de la coma esta bien" (Edson, 08/09). Cantidades de
// material (metros, unidades): SIEMPRE 2 decimales y separador de miles es-PY,
// sin símbolo de moneda. El valor exacto sigue viajando desde el backend; acá
// solo se redondea para mostrarlo.
export const formatCantidad = (value) => {
  const num = Number(value);
  if (value === null || value === undefined || isNaN(num)) return "0,00";
  return new Intl.NumberFormat("es-PY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatPYGCompact = (amount) => {
  if (!amount && amount !== 0) return "₲ 0";
  const num = Number(amount);
  if (num >= 1_000_000_000) return `₲ ${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `₲ ${(num / 1_000_000).toFixed(1)}M`;
  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency: "PYG",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatUSD = (amount) => {
  if (!amount && amount !== 0) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

// Fix FG (2026-10-08): montos en dólares con centavos (cobranzas, certificados y
// presupuestos en USD). Ej.: US$ 15.230,50
export const formatUSD2 = (amount) =>
  `US$ ${Number(amount || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// BLOQUE 17 — fix fechas desfasadas un día (timezone).
// Los campos "date-only" (YYYY-MM-DD, sin hora: target_date, deadline, fecha_estimada_fin,
// alquiler_fecha_limite, start_date/end_date, fecha_emision/cierre/pago, etc.) se parsean
// SIEMPRE por sus componentes Y-M-D como medianoche LOCAL (nunca vía UTC), para que el día
// mostrado sea siempre el mismo que el usuario tipeó, sin importar el huso horario del navegador.
// Las fechas con hora completa (created_at, timestamps de history, etc.) siguen usando el
// parseo nativo (ya correcto: son un instante real, se muestran convertidas a hora local).
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const parseDateOnly = (dateStr) => {
  if (typeof dateStr === "string" && DATE_ONLY_RE.test(dateStr)) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
};

// BLOQUE 35 fix (testing_agent iteration_129) — "hoy" en huso horario de
// Asunción (America/Asuncion), consistente con date_utils.today_asuncion()
// del backend (Bloque 17). Evita defaults de 1 día de desfase en inputs de
// fecha cuando el navegador del usuario corre en otro huso horario.
export const todayAsuncion = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Asuncion", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const get = (type) => parts.find(p => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};

// BLOQUE 55 — helpers de aritmética de fechas date-only (mismo criterio anti-
// desfase de arriba): siempre por componentes locales, nunca toISOString().
export const toDateOnlyStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const addDaysToDateOnly = (dateStr, days) => {
  const d = parseDateOnly(dateStr);
  d.setDate(d.getDate() + days);
  return toDateOnlyStr(d);
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return parseDateOnly(dateStr).toLocaleDateString("es-PY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export const formatDateShort = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return parseDateOnly(dateStr).toLocaleDateString("es-PY", { day: "2-digit", month: "short" });
  } catch {
    return dateStr;
  }
};

// Días restantes hasta dateStr (date-only), comparado contra "hoy" en medianoche LOCAL.
// Reemplaza a los daysUntil() duplicados que mezclaban Date UTC-parseado con "new Date()" local.
export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = parseDateOnly(dateStr);
  const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((targetMidnight - todayMidnight) / 86400000);
};

export const getStatusColor = (status) => {
  const map = {
    activo: "bg-emerald-100 text-emerald-700",
    completado: "bg-blue-100 text-blue-700",
    pausado: "bg-amber-100 text-amber-700",
    cancelado: "bg-red-100 text-red-700",
  };
  return map[status] || "bg-gray-100 text-gray-600";
};

export const getProgressColor = (progress) => {
  if (progress >= 90) return "bg-red-500";
  if (progress >= 70) return "bg-amber-500";
  return "bg-emerald-500";
};
